import { useState, useMemo } from 'react'
import { ProductCard } from './components/dataExplorer/ProductCard'
import { SearchFilters } from './components/dataExplorer/SearchFilters'
import {
  ContextSearchResult,
  ContextSearchResults,
} from 'store/types/searchResults'
import { componentPermissions } from 'config/permissions'
import { RoutePaths } from 'routes/paths'
import { componentNames, Roles } from 'config/roles'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'store'
import { useEffect } from 'react'
import { sagaGetSearchResultsAction } from 'saga/actions/assembler'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from './components/ui/pagination'
import ContentBodyWrapper from 'components/custom/ContentBodyWrapper'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { userReducerState } from 'store/slices/user'

const ProductListPage = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [classNameFilter, setClassNameFilter] = useState('all')
  const [groupFilter, setGroupFilter] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 25
  const dispatch = useDispatch()
  const { t } = useTranslation()

  useEffect(() => {
    dispatch(sagaGetSearchResultsAction('.standardParts'))
  }, [useDispatch])

  const searchResults: ContextSearchResults | undefined = useSelector(
    (state: RootState) => state.searchResults.searchResults?.data
  )
  // Options for the Country Autocomplete: use search results with contextName === '.energy'
  const standardPartsResults: ContextSearchResult[] = Array.isArray(
    searchResults
  )
    ? searchResults.filter((item) => item.contextName === '.standardParts')
    : []

  // Extract unique class names and groups
  const classNames = useMemo<string[]>(() => {
    return Array.from(
      new Set(
        standardPartsResults
          .map((p: ContextSearchResult) => p.fullContext?.className)
          .filter((x): x is string => typeof x === 'string')
      )
    )
  }, [standardPartsResults])

  const groups = useMemo<string[]>(() => {
    const productsToFilter =
      classNameFilter === 'all'
        ? standardPartsResults
        : standardPartsResults.filter(
            (p) => p.fullContext?.className === classNameFilter
          )

    const groupsArray = productsToFilter
      .map((p) => p.fullContext?.group)
      .filter((g): g is string => Boolean(g))

    return Array.from(new Set(groupsArray))
  }, [classNameFilter, standardPartsResults])

  // Filter products
  const filteredProducts = useMemo(() => {
    if (!standardPartsResults.length) return []
    return standardPartsResults.filter((product: ContextSearchResult) => {
      const matchesSearch =
        searchQuery === '' ||
        JSON.stringify(product.fullContext ?? {})
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        product.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchQuery.toLowerCase())

      const matchesClass =
        classNameFilter === 'all' ||
        product.fullContext?.className === classNameFilter

      const matchesGroup =
        groupFilter === 'all' || product.fullContext?.group === groupFilter

      return matchesSearch && matchesClass && matchesGroup
    })
  }, [searchQuery, classNameFilter, groupFilter, standardPartsResults])

  // Reset to page 1 when filters change
  useMemo(() => {
    setCurrentPage(1)
  }, [searchQuery, classNameFilter, groupFilter])

  // Reset group filter when class changes and current group is not available
  useMemo(() => {
    if (groupFilter !== 'all' && !groups.includes(groupFilter)) {
      setGroupFilter('all')
    }
  }, [classNameFilter, groups, groupFilter])

  // Calculate pagination
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentProducts = filteredProducts.slice(startIndex, endIndex)

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = []
    const showEllipsis = totalPages > 7

    if (!showEllipsis) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i)
        pages.push('ellipsis')
        pages.push(totalPages)
      } else if (currentPage >= totalPages - 2) {
        pages.push(1)
        pages.push('ellipsis')
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i)
      } else {
        pages.push(1)
        pages.push('ellipsis')
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i)
        pages.push('ellipsis')
        pages.push(totalPages)
      }
    }
    return pages
  }
  const { roles } = useSelector(userReducerState)
  const navigate = useNavigate()
  useEffect(() => {
    // ✅ Navigate to 403 if user does NOT have the correct role
    if (!roles?.includes(Roles.ROLE_COMMERCIAL_STANDARD_PARTS)) {
      navigate(RoutePaths.billing)
    }
  }, [roles, navigate])

  return (
    <ContentBodyWrapper title={'Product Explorer'}>
      <div className="container mx-auto px-4 py-8">
        <header className="mb-8">
          <p className="text-lg text-muted-foreground">
            Browse our catalog of standard parts and components
          </p>
        </header>

        <div className="mb-8">
          <SearchFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            classNameFilter={classNameFilter}
            onClassNameChange={setClassNameFilter}
            groupFilter={groupFilter}
            onGroupChange={setGroupFilter}
            classNames={classNames}
            groups={groups}
          />
        </div>

        <div className="mb-4 text-sm text-muted-foreground">
          Showing {startIndex + 1}-{Math.min(endIndex, filteredProducts.length)}{' '}
          of {filteredProducts.length} products
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {currentProducts.map((product: ContextSearchResult) => (
            <ProductCard key={product.sid} product={product} />
          ))}
        </div>

        {filteredProducts.length === 0 && (
          <div className="text-center py-16">
            <p className="text-lg text-muted-foreground">
              No products found matching your filters.
            </p>
          </div>
        )}

        {filteredProducts.length > 0 && totalPages > 1 && (
          <div className="mt-8">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    className={
                      currentPage === 1
                        ? 'pointer-events-none opacity-50'
                        : 'cursor-pointer'
                    }
                  />
                </PaginationItem>

                {getPageNumbers().map((page, index) => (
                  <PaginationItem key={index}>
                    {page === 'ellipsis' ? (
                      <PaginationEllipsis />
                    ) : (
                      <PaginationLink
                        onClick={() => setCurrentPage(page as number)}
                        isActive={currentPage === page}
                        className="cursor-pointer"
                      >
                        {page}
                      </PaginationLink>
                    )}
                  </PaginationItem>
                ))}

                <PaginationItem>
                  <PaginationNext
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    className={
                      currentPage === totalPages
                        ? 'pointer-events-none opacity-50'
                        : 'cursor-pointer'
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </div>
    </ContentBodyWrapper>
  )
}

export default {
  routeProps: {
    path: RoutePaths.toolboxStandardParts,
    title: 'Product Explorer',
    element: <ProductListPage />,
    roles: componentPermissions[componentNames.pageStandardParts],
    requiresAuthentication: true,
  },
  name: 'Standard Parts',
}
